import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const HSESummary = ({ data, onHSEUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for HSE changes
  React.useEffect(() => {
    if (!projectId) return;

    const hseChannel = moduleRealtimeService?.subscribeToHSEChanges(
      projectId,
      (payload) => {
        console.log('HSE change detected:', payload?.event);
        
        // Trigger HSE refresh when data changes
        if (onHSEUpdate) {
          onHSEUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(hseChannel);
    };
  }, [projectId, onHSEUpdate]);

  if (!data) {
    return (
      <div className="bg-card rounded-xl border border-border p-3 min-w-0">
        <h2 className="text-sm font-semibold text-foreground mb-3 truncate">HSE Overview</h2>
        <p className="text-muted-foreground text-xs">No HSE data available</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const metrics = [
    { label: 'Safety Incidents', value: data?.safetyIncidents, icon: 'AlertTriangle', color: data?.safetyIncidents > 0 ? 'var(--color-error)' : 'var(--color-success)' },
    { label: 'Near Misses', value: data?.nearMisses, icon: 'AlertCircle', color: 'var(--color-warning)' },
    { label: 'Training Completed', value: data?.safetyTrainingCompleted, icon: 'GraduationCap', color: 'var(--color-primary)' },
    { label: 'Environmental Violations', value: data?.environmentalViolations, icon: 'Leaf', color: data?.environmentalViolations > 0 ? 'var(--color-error)' : 'var(--color-success)' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-3 h-full min-w-0">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-foreground truncate">HSE Overview</h2>
        <Icon name="Shield" size={16} color="var(--color-primary)" />
      </div>

      <div className="space-y-2">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Total Incidents</span>
            <span className="text-lg font-bold text-foreground">{data?.totalIncidents}</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Minor</span>
            <span className="text-sm font-semibold text-warning">{data?.minorIncidents}</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Major</span>
            <span className="text-sm font-semibold text-error">{data?.majorIncidents}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Critical</span>
            <span className="text-sm font-semibold text-error">{data?.criticalIncidents}</span>
          </div>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Safety Audits</span>
            <span className="text-sm font-semibold text-foreground">{data?.safetyAudits}</span>
          </div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Training Sessions</span>
            <span className="text-sm font-semibold text-foreground">{data?.trainingSessions}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Compliance Rate</span>
            <span className="text-sm font-semibold text-success">{data?.complianceRate}%</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Days Since Last Incident</span>
            <span className="text-lg font-bold text-success">{data?.daysSinceLastIncident}</span>
          </div>
        </div>

        {data?.criticalIncidents > 0 && (
          <div className="p-2 bg-error/10 rounded-lg border border-error/20">
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
              <span className="text-xs font-medium text-error">
                {data?.criticalIncidents} Critical Incident{data?.criticalIncidents > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HSESummary;